import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Clock, Lock, Headphones } from "lucide-react";
import { Button } from "../../components/ui/button";
import GulfHeaderNav from "../../components/GulfHeaderNav";
import Logo from "../../components/Logo";
import { trackVisit, createOrder, trackCtaClick } from "../../lib/api";
import { trackClickButton, trackAddToCart, trackInitiateCheckout } from "../../lib/tiktok";
import { priceDisplay } from "../../lib/format";
import { useGeo } from "../../lib/useGeo";
import { toast } from "sonner";

const ABOUT_IMAGE = "/assets/hero-man.png";

export default function GulfAbout() {
  const navigate = useNavigate();
  const country = useGeo();
  const main = priceDisplay(27, country);

  useEffect(() => { trackVisit("gulf-about"); }, []);

  const buyNow = async () => {
    trackClickButton("about-buy");
    trackCtaClick("about-buy-cta");
    trackAddToCart("pdf_audio");
    trackInitiateCheckout("pdf_audio");
    try {
      const r = await createOrder("pdf_audio");
      navigate(`/checkout/${r.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء عملية الدفع، حاول مجدداً.");
    }
  };

  return (
    <div className="min-h-screen bg-[#040914] text-white" dir="rtl" data-testid="gulf-about-root">
      <GulfHeaderNav variant="static" />

      <section className="relative py-12 sm:py-20 border-b border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-3">عن الكتاب</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black leading-tight">
            ما هو <span className="text-[#C5A059]">الكتاب الأزرق</span>؟
          </h1>
          <p className="mt-5 sm:mt-7 text-sm sm:text-lg text-[#A1B0C6] leading-relaxed">
            الكتاب الأزرق هو دليل عملي للحضور والثقة والتعامل مع التوتر في المواقف الاجتماعية — خصوصاً مواقف التعارف والانطباع الأول، حيث يصبح كل تفصيل في كلامك وحضورك مؤثراً.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 grid lg:grid-cols-2 gap-10 sm:gap-12 items-center">
          <div className="flex justify-center order-2 lg:order-1">
            <img src={ABOUT_IMAGE} alt="الكتاب الأزرق" className="w-full max-w-sm rounded-lg shadow-[0_30px_60px_rgba(0,0,0,0.7),0_0_30px_rgba(197,160,89,0.18)]" draggable={false} />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-sm sm:text-base text-[#A1B0C6] leading-relaxed">
              ليس مجرد كتاب تنمية ذاتية. ولا "كن واثقاً من نفسك" مكرر. ولا قائمة جمل تحفظها وتلقيها.
            </p>
            <p className="mt-3 text-sm sm:text-base text-white/90 leading-relaxed">
              هو كتاب يأخذك خطوة بخطوة في فهم:
            </p>
            <ul className="mt-4 space-y-2.5 text-sm sm:text-base text-white/85">
              {[
                "كيف تبدأ الحوار بثبات، بدون توتر أو محاولة زائدة",
                "كيف تتعامل مع لحظات الصمت بدون ارتباك",
                "كيف تميّز بين الاهتمام الحقيقي والمجاملة",
                "كيف تفتح حواراً طبيعياً من الموقف نفسه",
                "كيف تبني انطباعاً أول أقوى من أول لحظة",
                "كيف تتكلم بثبات أمام من يهمك رأيه",
                "كيف تقلّل التفكير الزائد الذي يفسد اللحظة",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#C5A059] shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-10 sm:py-14 border-t border-[#C5A059]/10 bg-[#070f24]">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <div className="text-[#C5A059] text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] font-bold mb-3">عن المؤلف</div>
          <p className="text-[13px] sm:text-base text-[#A1B0C6] leading-relaxed">
            هذا الكتاب يرجع في الأساس للكاتب <span className="text-[#C5A059] font-bold">"جوردان بيترسون"</span> — عالم نفس وأستاذ جامعي عالمي، صاحب برامج وكتب أثّرت في حياة ملايين الرجال حول العالم، وكتبه تجاوزت مبيعاتها الـ <span className="text-[#C5A059] font-bold">10 مليون نسخة</span>. لقد درسنا أعماله بعناية، فهمنا كل أبعادها، وأعدنا صياغتها بما يتناسب مع عاداتنا وتقاليدنا.
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-20 border-t border-[#C5A059]/10">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display text-xl sm:text-3xl font-black">
            احصل على الكتاب الأزرق <span className="text-[#C5A059]">الآن</span>
          </h2>
          <div className="mt-2 text-sm text-[#A1B0C6]">{main.primary} {main.secondary ? `· ${main.secondary}` : ""} · يشمل النسخة الصوتية مجاناً</div>
          <Button data-testid="about-buy-btn" onClick={buyNow} className="mt-6 w-full max-w-sm bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-black text-base sm:text-lg py-7 rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse">
            افتح نسختك الآن · {main.primary}
          </Button>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] sm:text-xs text-[#A1B0C6]">
            <span className="flex items-center gap-1"><Headphones className="w-3.5 h-3.5 text-[#C5A059]" /> نسخة صوتية</span>
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-[#C5A059]" /> تسليم فوري</span>
            <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#C5A059]" /> دفع آمن</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#C5A059]" /> ضمان 7 أيام</span>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#C5A059]/15 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1B0C6]">
          <Logo className="h-9 w-9" />
          <div>© {new Date().getFullYear()} الكتاب الأزرق</div>
          <div className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-[#C5A059]" /> دفع آمن</div>
        </div>
      </footer>
    </div>
  );
}
